import React from 'react';
import { Box, Card, Divider, Typography } from '@mui/material';
import { OAuthProvider } from '@pepperhq/location-sdk';
import { useSnackbar } from 'notistack';

import {
    MESSAGE_INTEGRATION_DISCONNECT_ERROR,
    MESSAGE_INTEGRATION_DISCONNECT_SUCCESS,
    MESSAGE_INTEGRATION_OAUTH_CONNECT_ERROR
} from 'config/messages';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { ILocationScopedSettingsComponentProps } from 'components/settings/LocationScopedSettings';
import { SettingDisplay } from 'lib/SettingDisplay';
import { LoadingButton } from 'ui/buttons/LoadingButton';

const KLAVIYO = 'Klaviyo';

export const KlaviyoSettingsDisplay: React.FC<ILocationScopedSettingsComponentProps> = ({
    settings,
    onSettingsSaved
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const isAuthorized = React.useMemo(() => !!settings?.klaviyo?.oauth2AccessToken, [settings]);
    const [loadingOAuth, setLoadingOAuth] = React.useState(false);
    const [disconnecting, setDisconnecting] = React.useState(false);

    const handleConnectClicked = React.useCallback(async () => {
        setLoadingOAuth(true);
        try {
            // Klaviyo requires that the install URL has code_challenge, so get from menu
            // Navigate manually since 302 redirects needs CORS policy on Klaviyo
            const { url } = await new LocationSettingsApi().getOAuthInstallUrl(OAuthProvider.KLAVIYO);
            window.location.href = url;
        } catch {
            enqueueSnackbar(MESSAGE_INTEGRATION_OAUTH_CONNECT_ERROR(KLAVIYO), { variant: 'error' });
            setLoadingOAuth(false);
        }
    }, [enqueueSnackbar]);

    const handleDisconnectClicked = React.useCallback(async () => {
        setDisconnecting(true);
        try {
            await new LocationSettingsApi().revokeOAuthAccess(OAuthProvider.KLAVIYO);
            await onSettingsSaved();
            enqueueSnackbar(MESSAGE_INTEGRATION_DISCONNECT_SUCCESS(KLAVIYO), { variant: 'success' });
        } catch {
            enqueueSnackbar(MESSAGE_INTEGRATION_DISCONNECT_ERROR(KLAVIYO), { variant: 'error' });
        } finally {
            setDisconnecting(false);
        }
    }, [enqueueSnackbar, onSettingsSaved]);

    return (
        <Card sx={{ marginBottom: 2 }}>
            <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                padding={1}
                paddingLeft={2}
                minHeight={5.25}
            >
                <Typography variant="h6">Klaviyo</Typography>
            </Box>
            <Divider />
            <Box padding={2}>
                {isAuthorized ? (
                    <>
                        {settings?.klaviyo?.accountId && (
                            <SettingDisplay label="Account ID">
                                <Typography variant="body1">{settings.klaviyo.accountId}</Typography>
                            </SettingDisplay>
                        )}
                        <SettingDisplay label="Connection">
                            <LoadingButton
                                variant="outlined"
                                color="error"
                                onClick={handleDisconnectClicked}
                                loading={disconnecting}
                            >
                                Disconnect Klaviyo
                            </LoadingButton>
                        </SettingDisplay>
                    </>
                ) : (
                    <SettingDisplay
                        label="Connection"
                        description="Authorize Pepper to sync with your Klaviyo account."
                    >
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            onClick={handleConnectClicked}
                            loading={loadingOAuth}
                        >
                            Connect Klaviyo
                        </LoadingButton>
                    </SettingDisplay>
                )}
            </Box>
        </Card>
    );
};
